import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Database, ShieldCheck, Users, PlugsConnected, EnvelopeSimple } from "@phosphor-icons/react";
import StonLogo from "@/components/StonLogo";

const MOODBOARD_URL =
  "https://customer-assets-39nsmqrw.emergentagent.net/job_track-manufacturing/artifacts/ryk9mk4w_ChatGPT%20Image%20Aug%205%2C%202026%2C%2009_08_01%20PM.png";

const CONTACT_EMAIL = "Divyanshujangid11@gmail.com";

const SECTIONS = [
  {
    icon: Database,
    title: "Data collection",
    body: "We collect your name, email, contact number, and business details to provide real-time material tracking and quoting services.",
  },
  {
    icon: PlugsConnected,
    title: "Third-party services",
    body: "We use secure third-party services like Google/Apple for authentication and Amazon/Emergent for encrypted file storage.",
  },
  {
    icon: ShieldCheck,
    title: "Data security",
    body: "All technical specs, CAD drawings, and orders are stored in a secure database and encrypted during transit.",
  },
  {
    icon: Users,
    title: "User rights",
    body: (
      <>
        Users can request account deletion or data correction by contacting us at{" "}
        <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#8B4513] font-medium hover:underline" data-testid="privacy-contact-email">
          {CONTACT_EMAIL}
        </a>
        .
      </>
    ),
  },
];

export default function Privacy() {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-screen relative overflow-hidden text-zinc-900"
      data-testid="privacy-page"
      style={{
        backgroundImage: `url('${MOODBOARD_URL}')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Warm cream overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#f7f1e6]/92 via-[#efe6d4]/82 to-[#e6d9bc]/65 pointer-events-none" />

      <div className="relative min-h-screen flex flex-col">
        {/* Header */}
        <header className="flex items-center justify-between p-6 md:p-10">
          <StonLogo size={40} subtitle="Building Materials" className="text-zinc-900" data-testid="privacy-brand-logo" />
          <button
            type="button"
            onClick={() => navigate("/")}
            data-testid="privacy-back-link"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-[#8B4513] transition-colors"
          >
            <ArrowLeft size={16} weight="bold" /> Back to sign in
          </button>
        </header>

        {/* Body */}
        <main className="flex-1 flex flex-col items-center px-6 pb-16">
          <div className="w-full max-w-3xl">
            <div className="mt-tiny text-[#8B4513]">Legal</div>
            <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight text-zinc-900 mt-3 leading-[1.03]">
              Privacy Policy
            </h1>
            <p className="mt-3 mt-tiny text-zinc-500">Last updated: June 2026</p>

            {/* Introduction */}
            <div className="mt-8 p-6 bg-white/95 backdrop-blur-md border border-zinc-900/10 shadow-[0_10px_40px_-16px_rgba(60,40,10,0.35)]">
              <p className="text-zinc-700 text-lg leading-relaxed">
                STON LINK ARCHITECTURE MATERIALS is committed to protecting your privacy. This policy
                covers our Website and Mobile Apps (iOS and Android).
              </p>
            </div>

            {/* Sections */}
            <div className="mt-6 space-y-4">
              {SECTIONS.map(({ icon: Icon, title, body }) => (
                <section
                  key={title}
                  data-testid={`privacy-section-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className="p-6 bg-white/95 backdrop-blur-md border border-zinc-900/10 shadow-[0_10px_40px_-16px_rgba(60,40,10,0.35)]"
                >
                  <div className="flex items-center gap-3">
                    <div className="shrink-0 w-10 h-10 grid place-items-center bg-[#8B4513]/10 text-[#8B4513]">
                      <Icon size={20} weight="duotone" />
                    </div>
                    <h2 className="font-display text-xl font-bold text-zinc-900">{title}</h2>
                  </div>
                  <p className="mt-3 text-zinc-700 leading-relaxed">{body}</p>
                </section>
              ))}
            </div>

            {/* Contact footer */}
            <div className="mt-8 flex items-start gap-4 p-6 border border-dashed border-zinc-900/15">
              <EnvelopeSimple size={22} weight="duotone" className="text-[#8B4513] shrink-0 mt-0.5" />
              <p className="text-sm text-zinc-600 leading-relaxed">
                Questions about this policy or your data? Contact us at{" "}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#8B4513] font-medium hover:underline">
                  {CONTACT_EMAIL}
                </a>
                .
              </p>
            </div>

            <div className="mt-12 mt-tiny text-zinc-600/70">© 2026 Ston Link — Building Materials</div>
          </div>
        </main>
      </div>
    </div>
  );
}
